import React, { useMemo } from 'react';
import { Box, Typography } from '@mui/material';
import { FONT_ENUM } from '../../../types/global/fonts.types';
import type { IMessageAttachment } from '../../../redux/buzzbridge/campaignWorkspace.types';
import {
    extractLocationsFromContent,
    linkifyLine,
    locationsFromMessageAttachments,
    mergeLocationPoints,
} from '../../../utils/campaignThreadLocation';
import { CampaignThreadLocationCard, type CampaignThreadLocationVariant } from './CampaignThreadLocationCard';

const CREATOR_GOLD = '#ffc965';
const CREATOR_ON_GOLD = '#392700';
const BRAND_LINK = '#ffc965';

function LinkifiedText({
    content,
    variant,
    isMine,
    textColor,
}: {
    content: string;
    variant: CampaignThreadLocationVariant;
    isMine: boolean;
    textColor: string;
}) {
    const isCreator = variant === 'creator';
    const linkColor = isCreator ? (isMine ? CREATOR_ON_GOLD : CREATOR_GOLD) : BRAND_LINK;

    return (
        <Typography
            component="div"
            sx={{
                fontFamily: FONT_ENUM.MAIN_MEDIUM,
                fontSize: 14,
                lineHeight: 1.6,
                fontWeight: isCreator && isMine ? 600 : 400,
                whiteSpace: 'pre-wrap',
                wordBreak: 'break-word',
                color: textColor,
            }}>
            {content.split('\n').map((line, lineIndex, lines) => (
                <React.Fragment key={lineIndex}>
                    {linkifyLine(line).map((segment, segmentIndex) =>
                        segment.type === 'link' ? (
                            <Box
                                key={`${lineIndex}-${segmentIndex}`}
                                component="a"
                                href={segment.href}
                                target="_blank"
                                rel="noopener noreferrer"
                                sx={{
                                    color: linkColor,
                                    textDecoration: 'underline',
                                    textUnderlineOffset: 2,
                                    '&:hover': { opacity: 0.85 },
                                }}>
                                {segment.label}
                            </Box>
                        ) : (
                            <React.Fragment key={`${lineIndex}-${segmentIndex}`}>{segment.value}</React.Fragment>
                        ),
                    )}
                    {lineIndex < lines.length - 1 ? '\n' : null}
                </React.Fragment>
            ))}
        </Typography>
    );
}

export function CampaignThreadMessageContent({
    content,
    attachments = [],
    variant,
    isMine,
}: {
    content?: string;
    attachments?: IMessageAttachment[];
    variant: CampaignThreadLocationVariant;
    isMine: boolean;
}) {
    const { locations, remainingContent } = useMemo(() => {
        const fromAttachments = locationsFromMessageAttachments(attachments);
        const fromContent = extractLocationsFromContent(content ?? '');
        return {
            locations: mergeLocationPoints(fromAttachments, fromContent.locations),
            remainingContent: fromContent.remainingContent,
        };
    }, [attachments, content]);

    const isCreator = variant === 'creator';
    const textColor = isCreator ? (isMine ? CREATOR_ON_GOLD : 'rgba(255,255,255,0.9)') : 'rgba(255,255,255,0.82)';

    if (!remainingContent && locations.length === 0) return null;

    return (
        <>
            {remainingContent ? (
                <LinkifiedText content={remainingContent} variant={variant} isMine={isMine} textColor={textColor} />
            ) : null}
            {locations.map((location, index) => (
                <CampaignThreadLocationCard
                    key={`${location.lat}-${location.lng}-${index}`}
                    location={location}
                    variant={variant}
                    isMine={isMine}
                />
            ))}
        </>
    );
}
